import { createBaseApi, getAuthHeaders } from '../utils/baseApi';

const apiVersion = 'v1';

const {
  getJson,
  postJson,
  deleteJson,
  putJson
} = createBaseApi(`/api/${apiVersion}`, { headers: getAuthHeaders });

/*  Auth */

export const getUser = () => getJson('/me');

/* Attachment */

export const deleteAttachment = attachmentId => deleteJson(`attachments/${attachmentId}`);

export const getAttachment = attachmentId => getJson(`attachments/${attachmentId}`);

export const getAttachmentDownloadUrl = attachmentId => getJson(`attachments/${attachmentId}/download`);

export const createAttachment = ({
  attachableType,
  attachableId,
  filename,
  size
}) => postJson('attachments', { attachableType, attachableId, filename, size });

/* Codes */

export const getCompanyCodes = companyId => getJson(`companies/${companyId}/codes`);

/* Companies */
export const getCompany = (companyId, include) => getJson(`/companies/${companyId}`, include);

export const createCompany = companyInfo => postJson('/companies', companyInfo);

export const updateCompany = (companyId, companyInfo) => postJson(`/companies/${companyId}`, companyInfo);

export const updateCompanySetting = (companyId, settingTypeId, isEnabled, value) => putJson(`/companies/${companyId}/settings`, {
  company_setting_type_id: settingTypeId,
  is_enabled: isEnabled,
  value
});

export const listCompanies = (limit = true) => getJson(`/companies?limit=${limit}`);

export const getCompanyFeatures = companyId => getJson(`/companies/${companyId}/features`);

export const getCompanyIntegrations = companyId => getJson(`/companies/${companyId}/integrations`);

export const getCompanyInsureds = (companyId, pageData, filter, sort, include) => getJson(`/companies/${companyId}/insureds`, pageData, { filter }, { sort }, { include });

export const getCompanyJobs = (companyId, pageData, filter, sort, include) => getJson(`/companies/${companyId}/jobs`, pageData, { filter }, { sort }, { include });

export const getCompanyDepartments = (companyId, pageData, filters) => getJson(`/companies/${companyId}/departments`, pageData, filters);

export const getCompanyPolicyTypes = (companyId, filter) => getJson(`/companies/${companyId}/policy-types`, { filter });

export const getCompanyDocumentTypes = (companyId, filter) => getJson(`/companies/${companyId}/document-types`, { filter });

export const listCompanyRoles = companyId => getJson(`/companies/${companyId}/roles`);

export const getCompanyStats = companyId => getJson(`companies/${companyId}/stats`);

export const listCompanyInsuranceCompanies = (companyId, pageData, filter) => getJson(`/companies/${companyId}/insurance-companies`, pageData, { filter });

export const createInsuranceCompany = (companyId, insuranceCompanyName, naic = null) => postJson(`companies/${companyId}/insurance-companies`, { company_name: insuranceCompanyName, naic });

export const getCompanyCustomDomainDetails = companyId => getJson(`/companies/${companyId}/domains`);

export const createCompanyCustomDomain = (companyId, domain) => postJson(`/companies/${companyId}/domains`, { domain });

export const verifyCompanyCustomDomain = companyId => putJson(`/companies/${companyId}/domains`);

export const deleteCompanyCustomDomain = companyId => deleteJson(`/companies/${companyId}/domains`);

/* Departments */

export const getDepartment = departmentId => getJson(`/departments/${departmentId}`);

export const getDepartmentJobs = (departmentId, pageData, filters) => getJson(`/departments/${departmentId}/jobs`, { filters, ...pageData });

export const getDepartmentJobsWithStats = (departmentId, pageData, filters) => postJson(`/departments/${departmentId}/jobStats`, { filters, ...pageData });

/* Documents */

export const generateS3GetUrlForDocument = docId => getJson(
  `/insurance-documents/${docId}/generate-get-url`
);

export const createRecordDocument = (recordId, data) => postJson(`/records/${recordId}/documents`, data);

export const updateDocument = (docId, data) => putJson(`/insurance-documents/${docId}`, data);

export const prepareDocument = (docId, data) => putJson(`/insurance-documents/${docId}/prepare`, data);

export const startDocumentScan = docId => getJson(`/insurance-documents/${docId}/scan`);

export const pollDocumentScan = (docId, scanJobId) => putJson(`/insurance-documents/${docId}/scan`, { scan_job_id: scanJobId });

export const getDocumentScan = (docId, scanJobId) => postJson(`/insurance-documents/${docId}/scan`, { scan_job_id: scanJobId });

export const aiScanDocuments = ({ recordId, docIds, sync, publishAnswers }) => postJson(
  `/records/${recordId}/gpt-requests/record-questions`,
  { document_ids: docIds, sync, publish_answers: publishAnswers }
);

export const aiExtractPolicyDetailsFromDocuments = ({ recordId, docIds }) => postJson(
  `/records/${recordId}/gpt-requests/policies`,
  { document_ids: docIds }
);

export const getGptRequest = (recordId, gptRequestId) => getJson(`/records/${recordId}/gpt-requests/${gptRequestId}`);

export const publishGptRequestAnswers = ({ recordId, gptRequestId, sync }) => postJson(`/records/${recordId}/gpt-requests/${gptRequestId}`, { sync });

export const updateAssociatedPolices = (docId, data) => putJson(`/insurance-documents/${docId}/policies`, data);

export const searchInsuranceRequirements = (searchParams, pageData) => postJson('/insurance-requirements/search', { ...searchParams, ...pageData });

export const getInsuranceRequirementStats = (filters, companyIds = []) => postJson('/insurance-requirements/stats', { filters, company_ids: companyIds });

export const getRecord = id => getJson(`/records/${id}`);

export const updateRecord = (id, data) => putJson(`/records/${id}`, data);

export const updateRecordRequirements = (recordId, requirementTemplateId, { extractedTemplateIds = [], sourceTemplateId = null } = {}) => putJson(
  `/records/${recordId}/insurance-requirements`,
  {
    requirement_template_id: requirementTemplateId,
    ...(extractedTemplateIds.length ? { extracted_template_ids: extractedTemplateIds } : {}),
    ...(sourceTemplateId ? { source_template_id: sourceTemplateId } : {})
  }
);

export const archiveRecord = recordId => putJson(`/records/${recordId}/archive`);

export const reactivateRecord = recordId => putJson(`/records/${recordId}/reactivate`);

export const deleteRecord = recordId => deleteJson(`/records/${recordId}`);

export const getRecordPolicies = recordId => getJson(`/records/${recordId}/policies`);

export const getRecordDocuments = recordId => getJson(`/records/${recordId}/documents`);

export const getRecordWithRequirements = recordId => getJson(`/records/${recordId}/insurance-requirements`);

/* Activity Log */

export const searchInsuredActivity = (searchParams, pageData) => postJson('/insured-activities/search', { ...searchParams, ...pageData });

export const getInsuredTopLevelComments = (insuredId, pageData) => getJson(`/insureds/${insuredId}/comments`, pageData);

export const getActivitySubComments = (activityId, pageData) => getJson(`/insured-activities/${activityId}/comments`, pageData);

export const getActivitySubCommentsBatch = activityIds => getJson('/insured-activities/comments', { activity_ids: activityIds });

export const getCommentSubComments = (commentId, pageData) => getJson(`/comments/${commentId}/comments`, pageData);

export const getCommentSubCommentsBatch = commentIds => getJson('/comments/comments', { comment_ids: commentIds });

export const getCommentLinks = commentId => getJson(
  `/comments/${commentId}/links`
);

export const createInsuredActivityComment = (insuredId, data) => postJson(`/insureds/${insuredId}/comments`, data);

export const createRecordComment = (recordId, data) => postJson(`/insurance-requirements/${recordId}/comments`, data);

export const createCommentLink = (commentId, entityId, data) => postJson(`/comments/${commentId}/link/${entityId}`, data);

export const createActivitySubComment = (activityId, data) => postJson(`/insured-activities/${activityId}/comments`, data);

export const createCommentSubComment = (commentId, data) => postJson(`/comments/${commentId}/comments`, data);

export const updateComment = (commentId, data) => putJson(`/comments/${commentId}`, data);

export const deleteComment = commentId => deleteJson(`/comments/${commentId}`);

/* Email */

export const sendEmail = data => postJson('/emails/send-email', data);

export const sendBulkEmail = data => postJson('/emails/bulk', data);

export const getEmails = (insuredId, jobId, order, pageData) => {
  if (jobId && jobId !== 'all') {
    return getJson(`/insureds/${insuredId}/emails`, pageData, { job_id: jobId, order });
  }

  return getJson(`/insureds/${insuredId}/emails`, pageData, { order });
};

export const getEmailTemplates = (companyId, filter, pageData) => getJson(`companies/${companyId}/email-templates`, pageData, { filter });

export const getDefaultEmailTemplates = companyId => getJson(`/companies/${companyId}/default-email-templates`);

export const saveCompanyEmailTemplate = (id, data) => postJson(`/companies/${id}/email-templates`, data);

export const deleteEmailTemplate = id => deleteJson(`/email-templates/${id}`);

export const saveDepartmentEmailTemplate = (id, data) => postJson(`/departments/${id}/email-templates`, data);

export const previewEmail = (recordId, data) => postJson(`/records/${recordId}/preview-email`, data);

export const resendEmail = emailId => postJson(`/emails/${emailId}/resend-email`);

export const resendUserInviteEmail = (userId, companyId) => postJson(`/companies/${companyId}/users/${userId}/invite-emails`);

export const sendExportExcelEmail = (email, cc, searchParams, columns, companyId, withItems = true, withPoliciesAndRequirements = false, groupByCodes = false) => postJson('emails/export-report-excel', {
  email,
  cc,
  search_params: searchParams,
  columns,
  company_id: companyId,
  with_items: withItems,
  with_policies_and_requirements: withPoliciesAndRequirements,
  item_rows: groupByCodes
});

export const sendDownloadDocumentsEmail = (email, cc, searchParams, companyId) => postJson('emails/download-documents', {
  email,
  cc,
  search_params: searchParams,
  company_id: companyId
});

export const sendSubmissionEmail = (requirementId, submittedPolicies, submittedDocuments) => postJson('emails/submission-email', {
  requirement_id: requirementId,
  policies: submittedPolicies,
  documents: submittedDocuments
});

/* Insureds */
export const createInsured = (companyId, insuredInfo) => postJson(`companies/${companyId}/insureds`, insuredInfo);

export const getInsured = insuredId => getJson(`insureds/${insuredId}`);

export const updateInsured = (insuredId, updates) => putJson(`insureds/${insuredId}`, updates);

export const searchInsureds = (searchParams, pageData) => postJson('/insureds/search', { ...searchParams, ...pageData });

export const exportListInsuredsExcel = (companyId, searchParams) => postJson(`/companies/${companyId}/insureds/export-excel`, { searchParams });

export const createInsuranceRequirement = (insuredId, jobId, insuranceRequirementData) => postJson(
  `/insureds/${insuredId}/jobs/${jobId}/records`,
  insuranceRequirementData
);

export const bulkCreateInsuranceRequirements = (jobId, insuredIds, requirementTemplateId) => postJson(`/jobs/${jobId}/insurance-requirements`, {
  insured_ids: insuredIds,
  requirement_template_id: requirementTemplateId
});

export const updateOrCreateInsuredRequirementsTemplate = (requirementId, companyId) => postJson(`/update-requirements/${requirementId}`, { companyId });

export const archiveInsured = insuredId => putJson(`/insureds/${insuredId}/archive`);

export const reactivateInsured = insuredId => putJson(`/insureds/${insuredId}/reactivate`);

export const deleteInsured = insuredId => deleteJson(`/insureds/${insuredId}`);

/* Insurance Policies */

export const createInsuredInsurancePolicy = (insuredId, jobId, policyTypeId, details, properties) => postJson(`/insureds/${insuredId}/policies`, {
  policy_type_id: policyTypeId,
  details,
  properties,
  job_id: jobId
});

export const createRecordInsurancePolicy = (recordId, policyTypeId, details, properties) => postJson(`insurance-requirements/${recordId}/policies`, {
  policy_type_id: policyTypeId,
  details,
  properties
});

export const updateInsurancePolicy = (policyId, policyData) => putJson(`/policies/${policyId}`, policyData);

export const cloneRecordPoliciesAndDocuments = (recordId, policyIds, documentIds) => postJson(`/records/${recordId}/clones`, { policy_ids: policyIds, document_ids: documentIds });

/* Jobs */

export const createJobAttachment = ({ jobId, name, size }) => postJson(`jobs/${jobId}/attachments`, {
  jobId,
  name,
  size
});

export const listInsuredJobs = (insuredId, pageData, filter) => getJson(`insureds/${insuredId}/records`, pageData, { filter });

export const getInsuredOverallStatus = insuredId => getJson(`/insureds/${insuredId}/overall-status`);

export const getInsuredJobStatus = (insuredId, jobId) => getJson(`/insureds/${insuredId}/jobs/${jobId}/status`);

export const getJobStats = jobId => getJson(`/jobs/${jobId}/stats`);

export const getJob = (jobId, include = {}) => getJson(`/jobs/${jobId}`, include);

export const updateJob = (jobId, jobData) => putJson(`/jobs/${jobId}`, jobData);

export const exportListInsuranceRequirementsExcel = (jobId, searchParams) => postJson(`/jobs/${jobId}/export-excel`, { search_params: searchParams });

export const exportListJobsExcel = (companyId, searchParams) => postJson(`/companies/${companyId}/jobs/export-excel`, { searchParams });

export const searchJobs = (searchParams, pageData) => postJson('/jobs/search', { ...searchParams, ...pageData });

export const createNewJob = jobData => postJson('/jobs', jobData);

export const archiveJob = jobId => putJson(`/jobs/${jobId}/archive`);

export const reactivateJob = jobId => putJson(`/jobs/${jobId}/reactivate`);

export const deleteJob = jobId => deleteJson(`/jobs/${jobId}`);

/* Items */

export const getItemTypes = (id, include, pageData) => getJson(`/companies/${id}/item-types`, include, pageData);

export const getItems = (id, include, filter, pageData) => getJson(`/insureds/${id}/items`, pageData, { filter }, { include });

export const searchItems = (searchParams, pageData) => postJson('/items/search', { ...searchParams, ...pageData });

export const getItemsForPolicy = (id, insuredId, include, filter, pageData) => getJson(`/policies/${id}/items`, { insured_id: insuredId }, pageData, { filter }, { include });

export const updateItemCodeStatus = (itemId, codeId, status, comment) => postJson(`/items/${itemId}/codes/${codeId}/${status}`, { comment });

export const bulkUpdateItemCodeStatuses = (insuredId, codeId, status, itemSearchParams, comment) => postJson(`/insureds/${insuredId}/codes/${codeId}/bulk-update-statuses`, {
  status,
  comment,
  search_params: itemSearchParams
});

/* Admin — Mailgun delivery logs */

export const getMailgunLogs = ({ email, days = 7 }) => getJson('/admin/mailgun/logs', { email, days });

/* Imports (PINS admin bulk import tool) */

export const listImports = (companyId, { pageNumber = 1, pageSize = 25 } = {}) => getJson(`/companies/${companyId}/imports`, { page: { number: pageNumber, size: pageSize } });

export const createImport = (companyId, data) => postJson(`/companies/${companyId}/imports`, data);

export const getImport = importId => getJson(`/imports/${importId}`);

export const validateImport = importId => postJson(`/imports/${importId}/validate`, {});

export const runImport = importId => postJson(`/imports/${importId}/run`, {});

export const pauseImport = importId => postJson(`/imports/${importId}/pause`, {});

export const resumeImport = importId => postJson(`/imports/${importId}/resume`, {});

export const getImportRollbackPreview = importId => getJson(`/imports/${importId}/rollback`);

export const rollbackImport = importId => postJson(`/imports/${importId}/rollback`, {});

export const stageImportFromDropbox = importId => postJson(`/imports/${importId}/stage`, {});

export const cancelImportPull = importId => deleteJson(`/imports/${importId}/stage`);

export const listImportRows = (importId, { status = null, pageNumber = 1, pageSize = 50 } = {}) => getJson(`/imports/${importId}/rows`, {
  ...(status && { status }),
  page: { number: pageNumber, size: pageSize }
});

export const updateImportRow = (importId, rowId, overrides) => putJson(`/imports/${importId}/rows/${rowId}`, overrides);

export const registerImportManifest = importId => postJson(`/imports/${importId}/manifest`, {});

export const completeImportManifest = importId => postJson(`/imports/${importId}/manifest/complete`, {});

export const draftImportManifest = importId => postJson(`/imports/${importId}/manifest/draft`, {});

export const importManifestUrl = importId => `/api/${apiVersion}/imports/${importId}/manifest`;

export const classifyImportFiles = (importId, { all = false, tasks = ['file_type'] } = {}) => postJson(`/imports/${importId}/classify`, { all, tasks });

export const cancelClassifyImportFiles = importId => deleteJson(`/imports/${importId}/classify`);

export const listImportFiles = (importId, { status = null, search = null, pageNumber = 1, pageSize = 50 } = {}) => getJson(`/imports/${importId}/files`, {
  ...(status && { status }),
  ...(search && { search }),
  page: { number: pageNumber, size: pageSize }
});

export const deleteImportFile = (importId, fileId) => deleteJson(`/imports/${importId}/files/${fileId}`);

export const deleteImportFiles = (importId, { ids = null, all = false } = {}) => deleteJson(`/imports/${importId}/files`, all ? { all: true } : { ids });

export const registerImportFile = (importId, { path, size, contentType }) => postJson(`/imports/${importId}/files`, {
  path,
  size,
  ...(contentType && { content_type: contentType })
});

export const completeImportFile = (importId, fileId) => postJson(`/imports/${importId}/files/${fileId}/complete`, {});

export const importReportUrl = importId => `/api/${apiVersion}/imports/${importId}/report`;

/* Users */

export const getAgent = (agentId, jobId) => getJson(`/users/${agentId}/jobs/${jobId}/agent`);

export const createNewUser = userData => postJson('/users', userData);

export const updateUser = (userId, userData) => putJson(`users/${userId}`, userData);

export const searchUsers = ({ name = '', email = '', pageNumber = 1, pageSize = 25 } = {}) => getJson('/users', {
  filter: {
    ...(name && { name }),
    ...(email && { email })
  },
  page: {
    number: pageNumber,
    size: pageSize
  }
});

export const deactivateUser = (userId, companyId) => deleteJson(
  `/companies/${companyId}/users/${userId}`
);
export const activateUser = (companyId, userId) => putJson(
  `/companies/${companyId}/users/${userId}`
);

export const createUserRole = (userId, roleId, userData) => putJson(`/users/${userId}/roles/${roleId}`, userData);

export const deleteRoleUser = (userId, userData) => deleteJson(`/users/${userId}/role-user`, userData);

/* Company */
export const getCompanyUser = (companyId, userId) => getJson(`/companies/${companyId}/users/${userId}`);

export const getCompanyUsers = companyId => getJson(`/companies/${companyId}/users`);

export const createUser = (companyId, userData) => postJson(`/companies/${companyId}/users`, userData);

export const createUserCompanyRole = (companyId, userId, roleId) => postJson(
  `/companies/${companyId}/users/${userId}/roles/${roleId}`
);

export const deleteUserCompanyRole = (companyId, userId, roleId) => deleteJson(
  `/companies/${companyId}/users/${userId}/roles/${roleId}`
);

export const createUserDepartmentRole = (departmentId, userId, roleId) => postJson(
  `/departments/${departmentId}/users/${userId}/roles/${roleId}`
);

export const deleteUserDepartmentRole = (departmentId, userId, roleId) => deleteJson(
  `/departments/${departmentId}/users/${userId}/roles/${roleId}`
);

export const createDepartment = (companyId, departmentData) => postJson(`/companies/${companyId}/departments`, departmentData);

export const updateDepartment = (departmentId, departmentData) => putJson(`/departments/${departmentId}`, departmentData);

/* Procore insurance writeback */

export const getProcoreWriteback = companyId => getJson(`/companies/${companyId}/procore-writeback`);

export const getDepartmentProcoreWriteback = departmentId => getJson(`/departments/${departmentId}/procore-writeback`);

export const archiveDepartment = departmentId => putJson(
  `/departments/${departmentId}/archive`
);

export const transferDepartment = (fromDepartmentId, toDepartmentId) => postJson(`/departments/${fromDepartmentId}/transferDepartment`, { to_department_id: toDepartmentId });

export const activateDepartment = departmentId => putJson(
  `/departments/${departmentId}/activate`
);

export const deleteDepartment = departmentId => deleteJson(
  `/departments/${departmentId}`
);

/* Create Password */

export const validateUserCreatePassword = (userEmail, passwordKey) => getJson(`/users/${userEmail}/key/${passwordKey}`);

export const forgotPassword = email => postJson('/users/forgot-password', { email });

export const createUserPassword = (userId, userPassword, passwordKey) => putJson(`/users/${userId}/create-password`, {
  password: userPassword,
  key: passwordKey
});

/* User Settings */
export const getUserSettings = (userId, companyId) => getJson(`/users/${userId}/settings`, { company_id: companyId });

export const updateUserPassword = (userId, existingUserPassword, newUserPassword, confirmNewUserPassword) => putJson(
  `/users/${userId}/update-password`,
  { existing_password: existingUserPassword, password: newUserPassword, confirm_password: confirmNewUserPassword }
);

export const updateUserSetting = (userId, companyId, settingId, {
  isEnabled,
  value
}) => putJson(`users/${userId}/settings/${settingId}`, { company_id: companyId, is_enabled: isEnabled, value });

export const loginUser = (email, password) => postJson('login', { email, password });

/* Agent Portal */

export const verifySubmitterEmail = (insuranceRequestId, email) => postJson(`insurance-requests/${insuranceRequestId}/verify`, { email });

export const verifyOTP = insuranceRequestId => postJson(
  `insurance-requests/${insuranceRequestId}/verify-otp`
);

export const getInsuranceRequest = insuranceRequestId => getJson(`insurance-requests/${insuranceRequestId}/`);

export const getInsuranceRequestInsured = insuranceRequestId => getJson(`insurance-requests/${insuranceRequestId}/insureds`);

export const getInsuredCompany = insuranceRequestId => getJson(`insurance-requests/${insuranceRequestId}/company`);

export const createAgentInsuranceDocumentType = (insuredId, data) => postJson(`/insureds/${insuredId}/insurance-document-types`, data);

/* Requirement Templates */
export const createRequirementTemplateFolder = (companyId, data) => postJson(`/companies/${companyId}/requirement-template-folders`, data);

export const listRequirementTemplateFolders = (companyId, pageData) => getJson(`/companies/${companyId}/requirement-template-folders`, pageData);

export const updateRequirementTemplateFolder = (folderId, data) => putJson(`/requirement-template-folders/${folderId}`, data);

export const deleteRequirementTemplateFolder = folderId => deleteJson(`/requirement-template-folders/${folderId}`);

export const createRequirementTemplate = (companyId, data) => postJson(`/companies/${companyId}/requirement-templates`, data);

/* Add filter and sort options comment here, OR just build multiple redux methods */
export const listRequirementTemplates = (companyId, pageData, filter, sort) => getJson(`/companies/${companyId}/requirement-templates`, pageData, { filter }, { sort });

export const getRequirementTemplate = templateId => getJson(`/requirement-templates/${templateId}`);

export const updateRequirementTemplate = (templateId, data) => putJson(`/requirement-templates/${templateId}`, data);

export const deleteRequirementTemplate = templateId => deleteJson(`/requirement-templates/${templateId}`);

export const discardExtractedRequirementTemplate = templateId => deleteJson(`/requirement-templates/${templateId}/extraction-draft`);

export const createRequirementTemplateBlock = (templateId, data) => postJson(`/requirement-templates/${templateId}/requirement-template-blocks`, data);

export const updateRequirementTemplateBlock = (templateBlockId, data) => putJson(`/requirement-template-blocks/${templateBlockId}`, data);

export const deleteRequirementTemplateBlock = templateBlockId => deleteJson(
  `/requirement-template-blocks/${templateBlockId}`
);

export const createRequirementTemplateQuestion = (templateId, questionId, blockIds) => postJson(
  `/requirement-templates/${templateId}/requirement-template-questions`,
  { question_id: questionId, requirement_template_block_ids: blockIds }
);

export const deleteRequirementTemplateQuestion = (templateId, questionId) => deleteJson(`/requirement-templates/${templateId}/questions/${questionId}`);

export const deleteRequirementTemplateQuestionBlock = (templateQuestionId, templateBlockId) => deleteJson(
  `/requirement-template-questions/${templateQuestionId}/requirement-template-blocks/${templateBlockId}`
);

export const createInsurancePolicyType = (companyId, data) => postJson(`/companies/${companyId}/insurance-policy-types`, data);

export const updateInsurancePolicyType = (companyId, policyTypeId, data) => postJson(`/companies/${companyId}/insurance-policy-types/${policyTypeId}`, data);

export const listInsurancePolicyTypes = (companyId, pageData, filter) => getJson(`/companies/${companyId}/insurance-policy-types`, pageData, { filter });

export const deleteInsurancePolicyType = (companyId, policyTypeId) => deleteJson(
  `/companies/${companyId}/insurance-policy-types/${policyTypeId}`
);

export const createInsuranceDocumentType = (companyId, data) => postJson(`/companies/${companyId}/insurance-document-types`, data);

export const updateInsuranceDocumentType = (companyId, documentTypeId, data) => postJson(`/companies/${companyId}/insurance-document-types/${documentTypeId}`, data);

export const listInsuranceDocumentTypes = (companyId, pageData, filter) => getJson(`/companies/${companyId}/insurance-document-types`, pageData, { filter });

export const deleteInsuranceDocumentType = (companyId, documentTypeId) => deleteJson(
  `/companies/${companyId}/insurance-document-types/${documentTypeId}`
);

export const createInsuranceLanguage = (companyId, data) => postJson(`/companies/${companyId}/insurance-languages`, data);

export const updateInsuranceLanguage = (companyId, languageId, data) => postJson(`/companies/${companyId}/insurance-languages/${languageId}`, data);

export const listInsuranceLanguages = (companyId, pageData, filter) => getJson(`/companies/${companyId}/insurance-languages`, pageData, { filter });

export const deleteInsuranceLanguage = (companyId, languageId) => deleteJson(
  `/companies/${companyId}/insurance-languages/${languageId}`
);

export const addPolicyTypeFavorite = policyTypeId => postJson(
  `/insurance-policy-types/${policyTypeId}/requirement-type-favorites`
);

export const removePolicyTypeFavorite = policyTypeId => deleteJson(
  `/insurance-policy-types/${policyTypeId}/requirement-type-favorites`
);

export const addDocumentTypeFavorite = documentTypeId => postJson(
  `/insurance-document-types/${documentTypeId}/requirement-type-favorites`
);

export const removeDocumentTypeFavorite = documentTypeId => deleteJson(
  `/insurance-document-types/${documentTypeId}/requirement-type-favorites`
);

export const addLanguageFavorite = languageId => postJson(
  `/insurance-languages/${languageId}/requirement-type-favorites`
);

export const removeLanguageFavorite = languageId => deleteJson(
  `/insurance-languages/${languageId}/requirement-type-favorites`
);

export const getEmailAutomationTriggers = () => getJson('/email-automations/triggers');
export const getEmailAutomations = companyId => getJson(`/companies/${companyId}/email-automations`);
export const getEmailAutomationSequences = automationId => getJson(`/email-automations/${automationId}/sequences`);
export const createEmailAutomation = (companyId, data) => postJson(`/companies/${companyId}/email-automations`, data);
export const updateEmailAutomation = (automationId, data) => putJson(`/email-automations/${automationId}`, data);
export const deleteEmailAutomation = automationId => deleteJson(`/email-automations/${automationId}`);
export const createEmailAutomationStep = (automationId, data) => postJson(`/email-automations/${automationId}/steps`, data);
export const updateEmailAutomationStep = (stepId, data) => putJson(`/email-automation-steps/${stepId}`, data);
export const deleteEmailAutomationStep = stepId => deleteJson(`/email-automation-steps/${stepId}`);
export const triggerAutomation = automationId => postJson(`/email-automations/${automationId}`, { time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone });

export const submitRecordCertificate = (recordId, data) => postJson(`records/${recordId}/certificates`, data);
